import { useSidebarStore } from '@/stores/sidebar-store';
import { useTranslation } from '@kgc/i18n';
import { selectSortedFavorites, useFavoritesStore } from '@kgc/ui';
import { Star, X } from 'lucide-react';
import { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { DASHBOARD_NAV_ITEM, FLAT_NAV_ITEMS, NAV_GROUPS, type NavItem } from './nav-config';

interface SidebarProps {
  /** Wizard módban kényszerítetten collapsed — csak ikonok, menü továbbra is elérhető */
  forceCollapsed?: boolean;
}

export function Sidebar({ forceCollapsed = false }: SidebarProps) {
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useTranslation();
  // Store-ból olvassuk a collapsed állapotot
  // Wizard route-okon a sidebar alapból collapsed, de a user kézzel kinyithatja a >> gombbal
  const { mode, toggleCollapse } = useSidebarStore();
  const [userOverride, setUserOverride] = useState(false);
  const collapsed = userOverride ? mode === 'collapsed' : forceCollapsed || mode === 'collapsed';
  const [expandedItems, setExpandedItems] = useState<string[]>(['nav.rental', 'nav.worksheet']);

  // Reset user override when route changes (new wizard page → re-collapse)
  useEffect(() => {
    setUserOverride(false);
  }, [location.pathname]);

  // Favorites
  const favorites = useFavoritesStore(selectSortedFavorites);
  const { removeFavorite } = useFavoritesStore();

  const toggleExpanded = (label: string) => {
    setExpandedItems(prev =>
      prev.includes(label) ? prev.filter(l => l !== label) : [...prev, label]
    );
  };

  // All expandable labels (for expand/collapse all)
  const expandableLabels = NAV_GROUPS.flatMap(g => g.items)
    .filter(item => item.children)
    .map(item => item.label);

  const expandAll = () => setExpandedItems(expandableLabels);
  const collapseAll = () => setExpandedItems([]);

  const allExpanded = expandableLabels.every(label => expandedItems.includes(label));
  const allCollapsed = expandedItems.length === 0;

  const isActive = (href: string) => {
    if (href === '/dashboard') return location.pathname === href;
    return location.pathname.startsWith(href);
  };

  const isChildActive = (item: NavItem) => {
    return item.children?.some(child => isActive(child.href));
  };

  // ─── Single nav item renderer ─────────────────────────────────────────────

  const renderNavItem = (item: NavItem) => {
    if (item.href) {
      return (
        <button
          key={item.label}
          onClick={() => navigate(item.href!)}
          className={`flex w-full items-center gap-3 px-4 py-2 text-left transition-colors ${
            isActive(item.href)
              ? 'sidebar-active text-white'
              : 'text-[#1e4a52] dark:text-white/80 hover:bg-black/5 dark:hover:bg-white/10 hover:text-[#1e4a52] dark:hover:text-white'
          }`}
          title={collapsed ? t(item.label) : undefined}
        >
          <div className="nav-icon-badge" style={{ backgroundColor: item.iconColor ?? '#64748b' }}>
            <svg
              viewBox="0 0 24 24"
              fill="none"
              stroke="#fff"
              strokeWidth={2}
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <path d={item.icon} />
            </svg>
          </div>
          {!collapsed && <span className="text-sm">{t(item.label)}</span>}
        </button>
      );
    }

    return (
      <div key={item.label}>
        <button
          onClick={() => {
            if (collapsed && item.children?.[0]?.href) {
              navigate(item.children[0].href);
            } else {
              toggleExpanded(item.label);
            }
          }}
          className={`flex w-full items-center gap-3 px-4 py-2 text-left transition-colors ${
            isChildActive(item)
              ? 'bg-black/[0.06] dark:bg-white/10 text-[#1e4a52] dark:text-white'
              : 'text-[#1e4a52] dark:text-white/80 hover:bg-black/5 dark:hover:bg-white/10 hover:text-[#1e4a52] dark:hover:text-white'
          }`}
          title={collapsed ? t(item.label) : undefined}
        >
          <div className="nav-icon-badge" style={{ backgroundColor: item.iconColor ?? '#64748b' }}>
            <svg
              viewBox="0 0 24 24"
              fill="none"
              stroke="#fff"
              strokeWidth={2}
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <path d={item.icon} />
            </svg>
          </div>
          {!collapsed && (
            <>
              <span className="flex-1 text-sm">{t(item.label)}</span>
              <svg
                xmlns="http://www.w3.org/2000/svg"
                className={`h-4 w-4 transition-transform ${
                  expandedItems.includes(item.label) ? 'rotate-180' : ''
                }`}
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M19 9l-7 7-7-7"
                />
              </svg>
            </>
          )}
        </button>

        {/* Children */}
        {!collapsed && expandedItems.includes(item.label) && item.children && (
          <div className="bg-black/[0.03] dark:bg-black/20">
            {item.children.map(child =>
              child.external ? (
                <a
                  key={child.href}
                  href={child.href}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex w-full items-center gap-2 py-2 pl-12 pr-4 text-left text-sm text-[#3d6b73] dark:text-white/80 transition-colors hover:bg-black/[0.03] dark:hover:bg-white/5 hover:text-[#1e4a52] dark:hover:text-white"
                >
                  {t(child.label)}
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    className="h-3 w-3"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14"
                    />
                  </svg>
                </a>
              ) : (
                <button
                  key={child.href}
                  onClick={() => navigate(child.href)}
                  className={`flex w-full items-center gap-3 py-2 pl-12 pr-4 text-left text-sm transition-colors ${
                    isActive(child.href)
                      ? 'bg-[#14b8a6] text-white rounded'
                      : 'text-[#3d6b73] dark:text-white/80 hover:bg-black/[0.03] dark:hover:bg-white/5 hover:text-[#1e4a52] dark:hover:text-white'
                  }`}
                >
                  {t(child.label)}
                </button>
              )
            )}
          </div>
        )}
      </div>
    );
  };

  return (
    <aside
      className={`sidebar-bg flex flex-col text-[#1e4a52] dark:text-white transition-all duration-300 ${
        collapsed ? 'w-16' : 'w-60'
      }`}
    >
      {/* Logo */}
      <div
        className={`flex h-16 items-center border-b border-[#b8d8dd] dark:border-white/10 px-4 ${
          collapsed ? 'justify-center' : 'justify-between'
        }`}
      >
        {!collapsed && (
          <div className="flex items-center gap-2">
            <img src="/rpi-logo.png" alt="rPI ERP" className="h-8 w-auto dark:invert" />
            <span className="font-semibold">alpha v4.0</span>
          </div>
        )}
        <button
          onClick={e => {
            e.stopPropagation();
            setUserOverride(true);
            toggleCollapse();
          }}
          className="relative z-50 cursor-pointer rounded p-1.5 transition-colors hover:bg-black/10 dark:hover:bg-white/15 active:bg-black/20 dark:active:bg-white/25"
          title={collapsed ? t('nav.expand') : t('nav.collapse')}
          aria-label={collapsed ? t('nav.expand') : t('nav.collapse')}
          type="button"
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            className={`h-5 w-5 transition-transform ${collapsed ? 'rotate-180' : ''}`}
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M11 19l-7-7 7-7m8 14l-7-7 7-7"
            />
          </svg>
        </button>
      </div>

      {/* Navigation */}
      <nav className="flex-1 overflow-y-auto py-4">
        {/* Expand/Collapse All buttons */}
        {!collapsed && (
          <div className="mb-2 flex items-center justify-end gap-1 px-4">
            <button
              onClick={expandAll}
              disabled={allExpanded}
              className={`rounded p-1.5 text-xs transition-colors ${
                allExpanded
                  ? 'cursor-not-allowed text-[#5a8a92]/50 dark:text-white/50'
                  : 'text-[#3d6b73] dark:text-white/80 hover:bg-black/5 dark:hover:bg-white/10 hover:text-[#1e4a52] dark:hover:text-white'
              }`}
              title={t('nav.expandAll')}
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                className="h-4 w-4"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M4 8V4m0 0h4M4 4l5 5m11-1V4m0 0h-4m4 0l-5 5M4 16v4m0 0h4m-4 0l5-5m11 5l-5-5m5 5v-4m0 4h-4"
                />
              </svg>
            </button>
            <button
              onClick={collapseAll}
              disabled={allCollapsed}
              className={`rounded p-1.5 text-xs transition-colors ${
                allCollapsed
                  ? 'cursor-not-allowed text-[#5a8a92]/50 dark:text-white/50'
                  : 'text-[#3d6b73] dark:text-white/80 hover:bg-black/5 dark:hover:bg-white/10 hover:text-[#1e4a52] dark:hover:text-white'
              }`}
              title={t('nav.collapseAll')}
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                className="h-4 w-4"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M9 9V4.5M9 9H4.5M9 9L3.75 3.75M9 15v4.5M9 15H4.5M9 15l-5.25 5.25M15 9h4.5M15 9V4.5M15 9l5.25-5.25M15 15h4.5M15 15v4.5m0-4.5l5.25 5.25"
                />
              </svg>
            </button>
          </div>
        )}

        {/* Favorites Section */}
        {!collapsed && favorites.length > 0 && (
          <div className="mb-3 border-b border-[#b8d8dd] dark:border-white/10 pb-3">
            <div className="mb-1 flex items-center gap-2 px-4 text-xs font-semibold uppercase tracking-wider text-accent">
              <Star className="h-3 w-3 fill-current" />
              {t('nav.favorites')}
            </div>
            {favorites.map(fav => {
              const navItem = FLAT_NAV_ITEMS.find(
                item =>
                  item.label === fav.label || item.href === `/${fav.menuItemId.replace(/-/g, '/')}`
              );
              const href = navItem?.href ?? `/${fav.menuItemId.replace(/-/g, '/')}`;

              return (
                <div
                  key={fav.menuItemId}
                  className={`group flex items-center gap-2 px-4 py-1.5 text-sm transition-colors ${
                    isActive(href)
                      ? 'bg-accent/20 text-accent'
                      : 'text-[#3d6b73] dark:text-white/70 hover:bg-black/5 dark:hover:bg-white/10 hover:text-[#1e4a52] dark:hover:text-white'
                  }`}
                >
                  <button onClick={() => navigate(href)} className="flex-1 text-left">
                    {fav.label ?? navItem?.label ?? fav.menuItemId}
                  </button>
                  <button
                    onClick={e => {
                      e.stopPropagation();
                      removeFavorite(fav.menuItemId);
                    }}
                    className="rounded p-0.5 opacity-0 transition-opacity hover:bg-black/10 dark:hover:bg-white/20 group-hover:opacity-100"
                    title={t('nav.removeFavorite')}
                  >
                    <X className="h-3 w-3" />
                  </button>
                </div>
              );
            })}
          </div>
        )}

        {/* Dashboard — önálló, csoportok felett */}
        {renderNavItem(DASHBOARD_NAV_ITEM)}

        {/* NAV_GROUPS — 4 logikai csoport section headerekkel */}
        {NAV_GROUPS.map((group, groupIdx) => (
          <div key={group.groupLabel ?? groupIdx}>
            {/* Section separator + label */}
            {group.groupLabel && (
              <>
                {collapsed ? (
                  <hr className="my-2 border-[#b8d8dd]/60 dark:border-white/10 mx-2" />
                ) : (
                  <div className="mt-3 mb-1 border-t border-[#b8d8dd]/60 dark:border-white/10 pt-2 px-4">
                    <span className="text-[9px] font-semibold uppercase tracking-widest text-[#5a8a92] dark:text-white/60">
                      {t(group.groupLabel)}
                    </span>
                  </div>
                )}
              </>
            )}
            {group.items.map(item => renderNavItem(item))}
          </div>
        ))}
      </nav>

      {/* Special buttons */}
      <div className="border-t border-[#b8d8dd] dark:border-white/10 p-4">
        {/* MyForgeOS Button */}
        <button
          onClick={() => navigate('/myforgeos')}
          className={`group relative mb-2 flex w-full items-center gap-3 overflow-hidden rounded-lg px-3 py-2.5 text-sm font-medium transition-all duration-300 ${
            collapsed ? 'justify-center' : ''
          } bg-gradient-to-r from-purple-600 to-indigo-600 text-white hover:scale-[1.02]`}
          title="MyForgeOS"
          style={{
            boxShadow: '0 0 20px rgba(147, 51, 234, 0.4), 0 0 40px rgba(147, 51, 234, 0.2)',
          }}
        >
          <div className="absolute inset-0 bg-gradient-to-r from-purple-400/20 to-indigo-400/20 opacity-0 transition-opacity group-hover:opacity-100" />
          <svg
            xmlns="http://www.w3.org/2000/svg"
            className="h-5 w-5 flex-shrink-0"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M9.75 17L9 20l-1 1h8l-1-1-.75-3M3 13h18M5 17h14a2 2 0 002-2V5a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"
            />
          </svg>
          {!collapsed && <span className="relative z-10">MyForgeOS</span>}
        </button>

        {/* Help! Button */}
        <a
          href="https://mfl.support/"
          target="_blank"
          rel="noopener noreferrer"
          className={`group relative flex w-full items-center gap-3 overflow-hidden rounded-lg px-3 py-2.5 text-sm font-medium transition-all duration-300 ${
            collapsed ? 'justify-center' : ''
          } bg-gradient-to-r from-emerald-600 to-teal-600 text-white hover:scale-[1.02]`}
          title="Help!"
          style={{
            boxShadow: '0 0 20px rgba(16, 185, 129, 0.4), 0 0 40px rgba(16, 185, 129, 0.2)',
          }}
        >
          <div className="absolute inset-0 bg-gradient-to-r from-emerald-400/20 to-teal-400/20 opacity-0 transition-opacity group-hover:opacity-100" />
          <svg
            xmlns="http://www.w3.org/2000/svg"
            className="h-5 w-5 flex-shrink-0"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M8.228 9c.549-1.165 2.03-2 3.772-2 2.21 0 4 1.343 4 3 0 1.4-1.278 2.575-3.006 2.907-.542.104-.994.54-.994 1.093m0 3h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
            />
          </svg>
          {!collapsed && (
            <>
              <span className="relative z-10 flex-1">Help!</span>
              <svg
                xmlns="http://www.w3.org/2000/svg"
                className="h-3 w-3 opacity-60"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14"
                />
              </svg>
            </>
          )}
        </a>
      </div>
    </aside>
  );
}
